import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from "react";

export type FloatedRow = {
  left: number;
  width: number;
  bottom: number;
  height: number;
};

export function floatRowStyle(pin: FloatedRow | null): CSSProperties | undefined {
  if (!pin) return undefined;
  return {
    position: "fixed",
    left: pin.left,
    width: pin.width,
    bottom: pin.bottom,
    height: pin.height,
    zIndex: 8,
    boxSizing: "border-box",
  };
}

function snap(n: number) {
  return Math.round(n * 2) / 2;
}

function same(a: FloatedRow | null, b: FloatedRow | null) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.left === b.left && a.width === b.width && a.bottom === b.bottom && a.height === b.height;
}

/**
 * Keep an in-list add row in place while it is on screen. Once it slips below
 * the fold, pin it to the floor of `.al-shell-body` for as long as its list
 * is still in view.
 */
export default function useFloatRowInView(
  containerRef: RefObject<HTMLElement | null>,
  slotRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  layoutKey: string | number,
) {
  const [pin, setPin] = useState<FloatedRow | null>(null);
  const floatingRef = useRef(false);

  useLayoutEffect(() => {
    if (!enabled) {
      floatingRef.current = false;
      setPin(null);
      return;
    }
    const container = containerRef.current;
    const slot = slotRef.current;
    if (!container || !slot) return;
    const scroller = slot.closest(".al-shell-body");
    if (!(scroller instanceof HTMLElement)) return;

    let frame = 0;
    const measure = () => {
      frame = 0;
      const root = scroller.getBoundingClientRect();
      const block = container.getBoundingClientRect();
      const home = slot.getBoundingClientRect();
      const padBottom = parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
      const floor = root.bottom - padBottom;
      const height = snap(Math.max(slot.offsetHeight, 1));
      const containerInView = block.bottom > root.top + 4 && block.top < floor - 4;
      const belowFold = home.top > floor - 1;
      let float = floatingRef.current;
      if (!containerInView) float = false;
      else float = float ? home.top > floor - height - 6 : belowFold;
      floatingRef.current = float;
      const next: FloatedRow | null = float
        ? {
            left: snap(home.left),
            width: snap(home.width),
            bottom: snap(Math.max(0, window.innerHeight - floor)),
            height,
          }
        : null;
      setPin((prev) => (same(prev, next) ? prev : next));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    const io = new IntersectionObserver(schedule, { root: scroller, threshold: [0, 0.01, 1] });
    io.observe(container);
    io.observe(slot);
    const ro = new ResizeObserver(schedule);
    ro.observe(scroller);
    ro.observe(container);
    ro.observe(slot);
    scroller.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    measure();
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      io.disconnect();
      ro.disconnect();
      scroller.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [enabled, layoutKey, containerRef, slotRef]);

  return pin;
}
